import { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import worldAtlas from "world-atlas/countries-110m.json";
import { MapHoverCard } from "./MapHoverCard";
import { IranReportingMap } from "./IranReportingMap";
import { countRange } from "./model";
import { provinces } from "./geography";
import { useObservatoryCopy } from "./copy";
import type { Observatory } from "./schema";
const atlas = worldAtlas as unknown as { objects: { countries: never } };
const shapes = (
  feature(worldAtlas as never, atlas.objects.countries) as unknown as {
    features: Array<{ id: string | number; properties: { name?: string } }>;
  }
).features;
const path = geoPath(geoNaturalEarth1().scale(150).translate([480, 250]));
// Geometry is immutable; pointer previews must not reproject every country.
const projectedPaths = new Map(
  shapes.map((shape) => [shape, path(shape as never) ?? undefined]),
);
export function ReportingMap({
  data,
  location,
  onSelect,
  filters,
}: {
  data?: Observatory | null | undefined;
  location: string;
  onSelect: (location: string) => void;
  filters?: { community: string; platform: string; threat: string };
}) {
  const { locale, t } = useObservatoryCopy();
  const tooltipId = useId();
  const mapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{
    location: string;
    name: string;
    x: number;
    y: number;
    width: number;
  } | null>(null);
  function clearHover() {
    mapRef.current
      ?.querySelectorAll("[aria-describedby]")
      .forEach((node) => node.removeAttribute("aria-describedby"));
    setHover(null);
  }
  function showHover(target: Element, clientX?: number, clientY?: number) {
    const area = target.closest<SVGPathElement>("path[data-location]");
    const rect = mapRef.current?.getBoundingClientRect();
    if (!area || !rect) return clearHover();
    const box = area.getBoundingClientRect();
    const width = Math.min(252, Math.max(120, rect.width - 16));
    const x = Math.max(
      8,
      Math.min(
        rect.width - width - 8,
        (clientX ?? box.x + box.width / 2) - rect.x + 14,
      ),
    );
    const y = Math.max(
      8,
      Math.min(
        Math.max(8, rect.height - 224),
        (clientY ?? box.y + box.height / 2) - rect.y - 100,
      ),
    );
    mapRef.current
      ?.querySelectorAll("[aria-describedby]")
      .forEach((node) => node.removeAttribute("aria-describedby"));
    area.setAttribute("aria-describedby", tooltipId);
    setHover({
      location: area.dataset.location!,
      name: area.dataset.areaName!,
      x,
      y,
      width,
    });
  }
  useLayoutEffect(() => {
    clearHover();
  }, [
    location,
    locale,
    filters?.community,
    filters?.platform,
    filters?.threat,
  ]);
  const selectedProvince = useMemo(
    () =>
      Object.entries(provinces).find(
        ([, p]) => `province:${p.name}` === location,
      )?.[0],
    [location],
  );
  const iran = location === "country:364" || location.startsWith("province:");
  const scoped = (data?.views ?? []).filter(
    (v) =>
      v.community === (filters?.community ?? "") &&
      v.platform === (filters?.platform ?? "") &&
      v.threat === (filters?.threat ?? ""),
  );
  const areaViews = scoped.filter((v) =>
    v.location.startsWith(iran ? "province:" : "country:"),
  );
  const maximum = Math.max(1, ...areaViews.map((v) => v.cases.lower));
  const color = (value: number) =>
    value / maximum >= 0.7
      ? "#52b8b5"
      : value / maximum >= 0.4
        ? "#92d7d2"
        : "#c4e9e5";
  return (
    <section className="observatory-panel observatory-map">
      <h2 className="observatory-sr-only">
        {iran ? t("iran") : t("mapTitle")}
      </h2>
      <div className="observatory-map-layout">
        <div
          className="observatory-map-viz"
          ref={mapRef}
          onMouseMove={(event) =>
            showHover(event.target as Element, event.clientX, event.clientY)
          }
          onMouseLeave={clearHover}
          onFocusCapture={(event) => showHover(event.target as Element)}
          onBlurCapture={clearHover}
          onKeyDownCapture={(event) => {
            if (event.key === "Escape") clearHover();
          }}
        >
          {iran ? (
            <IranReportingMap
              selected={selectedProvince}
              views={areaViews}
              color={color}
              onSelect={onSelect}
            />
          ) : (
            <svg
              viewBox="55 35 850 425"
              className="observatory-world"
              role="group"
              aria-label={t("world")}
            >
              {shapes
                .filter((shape) => shape.properties.name !== "Antarctica")
                .map((shape, i) => {
                  const code = String(shape.id).padStart(3, "0");
                  const publishedView = areaViews.find(
                    (v) => v.location === `country:${code}`,
                  );
                  const published = !!publishedView;
                  const selectable = published || code === "364";
                  const name =
                    data?.countries[code] ??
                    shape.properties.name ??
                    t("unknown");
                  return (
                    <path
                      key={`${code}-${i}`}
                      d={projectedPaths.get(shape)}
                      className={`${published ? "published" : ""} ${location === `country:${code}` ? "selected" : ""}`}
                      style={
                        publishedView && location !== `country:${code}`
                          ? { fill: color(publishedView.cases.lower) }
                          : undefined
                      }
                      role={selectable ? "button" : "img"}
                      tabIndex={selectable ? 0 : undefined}
                      aria-label={`${name} · ${publishedView ? countRange(publishedView.cases) : t("unpublishedAreas")}`}
                      aria-pressed={
                        selectable ? location === `country:${code}` : undefined
                      }
                      data-location={`country:${code}`}
                      data-area-name={name}
                      onClick={() => selectable && onSelect(`country:${code}`)}
                      onKeyDown={(e) => {
                        if (
                          selectable &&
                          (e.key === "Enter" || e.key === " ")
                        ) {
                          e.preventDefault();
                          onSelect(`country:${code}`);
                        }
                      }}
                    />
                  );
                })}
            </svg>
          )}
          {hover && (
            <MapHoverCard
              id={tooltipId}
              name={hover.name}
              view={areaViews.find((v) => v.location === hover.location)}
              x={hover.x}
              y={hover.y}
              width={hover.width}
            />
          )}
        </div>
        <aside className="observatory-map-key">
          <strong>{iran ? t("iran") : t("world")}</strong>
          <span>
            <i className="selected-area" />
            <span>
              {t("selectedArea")}
              <small>
                {selectedProvince
                  ? locale === "fa"
                    ? provinces[+selectedProvince]!.fa
                    : provinces[+selectedProvince]!.name
                  : location.startsWith("country:")
                    ? (data?.countries[location.split(":")[1]!] ?? t("unknown"))
                    : t("any")}
              </small>
            </span>
          </span>
          <span>
            <i className="published-area" />
            {t("higherReporting")}
          </span>
          <span>
            <i className="lower-reporting-area" />
            {t("lowerReporting")}
          </span>
          <span>
            <i className="unpublished-area" />
            {t("unpublishedAreas")}
          </span>
        </aside>
      </div>
      {!data && <p role="status">{t("legacy")}</p>}
    </section>
  );
}
